import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Ticket,
  Tag,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { api } from "../../redux/apis/api";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";
import { canAdd, resolvePolicy } from "./benefit-policy";
import { recomputeBillTotal } from "./billing-totals";

// Compact coupon-entry widget rendered above the LoyaltyPanel inside
// billing-payment-panel.js. The cashier types a coupon code, the engine
// previews the discount (GET /coupons/code/:code), and on confirm the apply
// RPC (POST /coupons/apply) records a coupon_usage row + bumps counters.
//
// Coupons reduce the subtotal — NOT a payment-mode tender. So the applied
// discount is written into the bill's discount section, not paymentData.
// See docs/coupons-system.md §14 for the engine contract.

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const fmtINR = (n) => Number(n || 0).toFixed(2);

// Friendly copy for each engine error code (docs §14).
const ERROR_COPY = {
  COUPON_NOT_FOUND: "Coupon code not found.",
  COUPON_NOT_ACTIVE: "This coupon is not active.",
  CODE_DISABLED: "This coupon code has been disabled.",
  CODE_USED: "This coupon code has already been used.",
  OUT_OF_WINDOW: "Coupon is outside its validity window.",
  EXPIRED: "This coupon has expired.",
  COUPON_EXHAUSTED: "Coupon usage limit reached.",
  PER_USER_CAP: "You have already used this coupon the maximum number of times.",
  MIN_BILL_NOT_MET: "Bill total is below the minimum required for this coupon.",
  MIN_QTY_NOT_MET: "Cart quantity is below the minimum required for this coupon.",
  SCOPE_MISMATCH: "Coupon does not apply to the items in this bill.",
  TIER_INELIGIBLE: "Customer tier is not eligible for this coupon.",
  FIRST_PURCHASE_ONLY: "Coupon is only valid on first purchase.",
  STACKING_BLOCKED: "Coupon cannot be combined with the current discounts.",
};

const explainError = (code, fallback) =>
  ERROR_COPY[code] || fallback || code || "Coupon could not be applied.";

const CouponPanel = ({
  cmrId,
  phoneE164,
  storeId,
  cashierId,
  billTotal,
  billRef,
  paymentData, // unused for write, kept for parity / future stacking gates
  setFieldValue,
  values, // optional — when present we surface/write the discount line cleanly
  prefixName = "sot",
}) => {
  // UI state machine — idle | previewing | preview | applying | applied | error
  // Hydrate `applied` from Formik on mount. The side panel mounts/unmounts
  // on close/open, dropping local state — but Formik state (couponDiscount)
  // survives. Without this, reopening the panel left the breakdown row
  // visible while the panel had no way to render the Remove button.
  const hydrated = values?.couponDiscount || null;
  const [stage, setStage] = useState(hydrated ? "applied" : "idle");
  const [codeInput, setCodeInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(hydrated);
  const [errorState, setErrorState] = useState(null);
  const [busy, setBusy] = useState(false);

  // Snapshot the code/preview at the time the cashier confirmed so a stale
  // bill-total tick can't race the apply RPC. The engine re-validates on
  // its side; this is a UX guardrail only.
  const confirmedRef = useRef(null);

  // Track the (cmrId, phoneE164) we last saw so a customer SWITCH (not the
  // initial mount) is what triggers the reset. On the initial mount we want
  // to PRESERVE hydration from Formik — clearing here would defeat that.
  const lastCmrSig = useRef(`${cmrId || ""}|${phoneE164 || ""}`);
  useEffect(() => {
    const nextSig = `${cmrId || ""}|${phoneE164 || ""}`;
    if (lastCmrSig.current === nextSig) return;
    lastCmrSig.current = nextSig;
    // Real customer change — coupon eligibility depends on the customer
    // (per-user caps, tier scope, first-purchase rule). Clear everything,
    // both local state and the Formik fields, so the next preview starts
    // from a clean slate.
    setStage("idle");
    setCodeInput("");
    setPreview(null);
    setApplied(null);
    setErrorState(null);
    confirmedRef.current = null;
    clearDiscountLine();
    window.__lastCouponApplied = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmrId, phoneE164]);

  // Mount hydration: if Formik has a `couponDiscount` AND the parent's
  // total_amount got reset (e.g. by a cart-lines recalc that doesn't know
  // about coupons), re-apply the discount line so Net Payable matches the
  // breakdown. Idempotent — skips if total_amount already reflects the
  // discount.
  const hydratedOnceRef = useRef(false);
  useEffect(() => {
    if (hydratedOnceRef.current) return;
    if (!applied) return;
    hydratedOnceRef.current = true;
    const current = parseFloat(values?.[`${prefixName}_total_amount`] || 0);
    const orig = parseFloat(values?.[`${prefixName}_original_total_amount`] || 0);
    const discount = parseFloat(applied.discount_amount || 0);
    // If original is missing OR current total already includes the discount,
    // skip. Otherwise re-write the line so the math reconciles.
    if (!orig || Math.abs(current - (orig - discount)) > 0.5) {
      writeDiscountLine(applied);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied]);

  // If the bill total drops below the coupon's min_purchase after preview,
  // surface a refresh hint rather than silently keeping a stale preview.
  const billRefTotal = Number(billTotal || 0);
  const previewStale =
    preview && Number(preview.bill_total || billRefTotal) !== billRefTotal;

  const trimmedCode = useMemo(
    () => (codeInput || "").trim().toUpperCase(),
    [codeInput]
  );

  const lookupPreview = async () => {
    if (!trimmedCode) {
      errorToast("Enter a coupon code first.");
      return;
    }
    if (billRefTotal <= 0) {
      errorToast("Add items to the bill before applying a coupon.");
      return;
    }
    setBusy(true);
    setStage("previewing");
    setErrorState(null);
    try {
      // Preview with the REAL cart lines through POST /coupons/preview —
      // the same validateAtBill the bill commit runs. The old GET lookup
      // synthesized ONE average-priced line from bill_total/total_qty, so
      // BOGO/line-sensitive coupons previewed a different discount than the
      // commit recomputed → BILL_TOTAL_MISMATCH at save.
      //
      // Lines use the SAME basis as the backend commit snapshot:
      //   unit_price = line_total / qty; scheme-reward rows flagged
      //   is_offer_item (excluded from BOGO bundles server-side).
      // Total is the PRE-BENEFIT payable (the original anchor when another
      // benefit already reduced the visible total) — the commit validates
      // percent coupons against that same base.
      const cartRows = Array.isArray(values?.data) ? values.data : [];
      const lines = cartRows
        .filter((r) => r && (r.item_id || r.product_id))
        .map((r) => {
          const qty = Number(r?.quantity ?? r?.qty ?? 0);
          const lineTotal = Number(r?.item_total_amount ?? 0);
          const unitNet =
            qty > 0 && lineTotal > 0
              ? Math.round((lineTotal / qty) * 100) / 100
              : Number(r?.item_unit_price ?? r?.unit_price ?? 0);
          return {
            product_id: String(r.item_id || r.product_id || ""),
            category_id: r.item_category_id || r.category_id || null,
            qty,
            unit_price: unitNet,
            is_offer_item:
              !!r?.__scheme_added_by_id ||
              r?.is_promotional === true ||
              r?.is_offer_item === true,
          };
        });
      const previewBase = Number(
        values?.[`${prefixName}_original_total_amount`] ?? billRefTotal
      );
      const res = await api.post(`/coupons/preview`, {
        code: trimmedCode,
        bill: { total: previewBase, lines },
        cmr_id: cmrId || undefined,
        tier_code: values?.cmr_tier_code || undefined,
        is_first_purchase: values?.is_first_purchase === true,
      });
      const body = res?.data || {};
      if (body.status === false) {
        const code = body?.data?.code || body?.code || "COUPON_NOT_FOUND";
        setErrorState({ code, message: body.message });
        setStage("error");
        return;
      }
      const data = body.data || {};
      // The lookup endpoint returns `discount` (no _amount suffix). The rest
      // of this file reads `discount_amount` everywhere. Normalize here so
      // both old and new shapes work and the preview banner stops showing
      // "− ₹0.00" against a backend that's actually returning a real value.
      setPreview({
        ...data,
        discount_amount: round2(data.discount_amount ?? data.discount ?? 0),
        bill_total: billRefTotal,
      });
      setStage("preview");
    } catch (err) {
      const errBody = err?.response?.data || {};
      const code = errBody?.data?.code || errBody?.code || "COUPON_NOT_FOUND";
      setErrorState({ code, message: errBody.message });
      setStage("error");
    } finally {
      setBusy(false);
    }
  };

  const confirmApply = async () => {
    if (!preview) return;
    // Benefit-policy gate: if loyalty has already loaded a member, use its
    // policy; else fall back to the migration default. Coupons being a
    // "kind" peer of points + schemes, this prevents the cashier from
    // stacking past the policy limit (one / two / all).
    const policyName = resolvePolicy(window.__loyaltyMember);
    const ok = canAdd("coupon", values, policyName);
    if (!ok.ok) {
      errorToast(ok.reason);
      return;
    }
    // Guard against the case where billTotal updated AFTER the preview rendered
    // but BEFORE the click event landed. Without this, writeDiscountLine below
    // would commit the stale discount amount. (Review P0.)
    if (previewStale) {
      errorToast("Bill total changed — preview again.");
      return;
    }
    // Atomic-apply: stash the coupon identity in Formik. The bill-save
    // dispatch reads couponDiscount.coupon_code and ships it as
    // benefits.coupon.code; the backend re-validates + writes the
    // coupon_usage audit row INSIDE the bill-INSERT transaction. No
    // separate /coupons/apply RPC — either the bill saves with the
    // coupon's discount AND audit row, or neither lands.
    const discountAmount = round2(preview.discount_amount || 0);
    confirmedRef.current = {
      code: trimmedCode,
      engine_code: preview.code,
      amount: discountAmount,
    };
    const synced = {
      coupon_code: trimmedCode,
      coupon_code_id: preview.coupon_code_id || preview.code_id,
      coupon_id: preview.coupon_id,
      coupon_name: preview.coupon_name || preview.name || trimmedCode,
      discount_type: preview.discount_type,
      discount_amount: discountAmount,
    };
    setApplied(synced);
    window.__lastCouponApplied = synced;
    writeDiscountLine(synced);
    setStage("applied");
    successToast(`Coupon applied — ₹${fmtINR(synced.discount_amount)} off`);
  };

  // ----- Discount-line write-through -------------------------------------
  //
  // Coupons reduce the bill subtotal (NOT a payment-mode tender). Zota's
  // existing discount shape on the sales-order Formik state is:
  //
  //   values.{prefix}_discount_amount  – flat ₹ off
  //   values.couponDiscount            – metadata object (new field)
  //
  // The {prefix}_total_amount recompute path lives in the parent invoice
  // form (invoice/sales-order pages). We deliberately do NOT recompute the
  // total here — that's the parent's job — but we DO emit a clear console
  // warning so the integrator can wire the total-recalc effect.
  const writeDiscountLine = (line) => {
    if (typeof setFieldValue !== "function") {
      console.warn(
        "[CouponPanel] setFieldValue missing — cannot write coupon discount line"
      );
      return;
    }
    setFieldValue("couponDiscount", line);
    // Bump the dedicated coupon-discount field if the form uses one.
    setFieldValue(`${prefixName}_coupon_discount`, line.discount_amount);
    // Shared recompute keeps coupon + scheme + points consistent. Passing
    // the just-applied discount via override.coupon because setFieldValue
    // above is async-batched and `values.couponDiscount` still holds the
    // old value during this same tick.
    recomputeBillTotal({
      values,
      setFieldValue,
      prefixName,
      override: { coupon: line.discount_amount },
    });
  };

  const clearDiscountLine = () => {
    if (typeof setFieldValue !== "function") return;
    setFieldValue("couponDiscount", null);
    setFieldValue(`${prefixName}_coupon_discount`, 0);
    // Recompute with coupon zeroed out — keeps any active scheme / points
    // discount in the total. Restoring to original would wrongly drop them.
    recomputeBillTotal({
      values,
      setFieldValue,
      prefixName,
      override: { coupon: 0 },
    });
  };

  const removeApplied = async () => {
    if (!applied) return;
    setBusy(true);
    try {
      // Atomic-apply: coupon_usage row only exists if the bill has been
      // committed (usage_id is the server-issued id from applyCoupon, which
      // now runs INSIDE the bill INSERT txn — not here). For a coupon that
      // was only staged in Formik and never committed, there's nothing
      // server-side to reverse — just clear the UI.
      if (applied.usage_id) {
        await api.post(
          `/coupons/reverse`,
          JSON.stringify({
            code: applied.coupon_code,
            coupon_code_id: applied.coupon_code_id,
            coupon_id: applied.coupon_id,
            bill_id: applied.bill_ref,
            usage_id: applied.usage_id,
            cmr_id: cmrId || null,
            store_id: storeId || null,
          })
        );
      }
    } catch (err) {
      // Reversal failure here is non-fatal at the UI — the bill-cancel /
      // bill-return path on the server will handle the audit reversal too.
      // We still drop the line so the cashier can re-try a different code.
      console.warn(
        "[CouponPanel] reverse RPC failed; clearing UI anyway",
        err?.response?.data || err?.message
      );
    } finally {
      setBusy(false);
      window.__lastCouponApplied = null;
      setApplied(null);
      setCodeInput("");
      setPreview(null);
      setErrorState(null);
      setStage("idle");
      clearDiscountLine();
      successToast("Coupon removed");
    }
  };

  const retry = () => {
    setErrorState(null);
    setStage("idle");
    setPreview(null);
  };

  // ---- Render: applied --------------------------------------------------
  if (stage === "applied" && applied) {
    return (
      <div className="rounded-xl border border-[#BBE7CC] bg-gradient-to-br from-[#F0FBF4] to-[#E6F6EC] px-3 py-2.5 mb-3 flex items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
          style={{
            background: "linear-gradient(135deg, #22C55E, #15803C)",
            color: "white",
          }}
        >
          <Ticket size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-[#15803C] font-bold">
            Coupon applied
          </div>
          <div className="text-[14px] font-bold text-[#0F172A] mt-0.5 flex items-center gap-2 flex-wrap">
            <span className="font-mono tracking-wide">{applied.coupon_code}</span>
            {applied.coupon_name && (
              <span className="text-[12px] font-semibold text-[#475569] truncate">
                {applied.coupon_name}
              </span>
            )}
            <span className="text-[13px] text-[#15803C]">
              − ₹{fmtINR(applied.discount_amount)}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={removeApplied}
          disabled={busy}
          title="Remove coupon"
          className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md text-[12px] font-semibold text-[#15803C] hover:bg-white/60 disabled:opacity-50"
        >
          <XCircle size={14} /> {busy ? "…" : "Remove"}
        </button>
      </div>
    );
  }

  // ---- Render: error ----------------------------------------------------
  if (stage === "error" && errorState) {
    return (
      <div className="rounded-xl border border-[#FCA5A5] bg-gradient-to-br from-[#FEF2F2] to-[#FEE2E2] px-3 py-2.5 mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 shadow-sm"
            style={{
              background: "linear-gradient(135deg, #EF4444, #B91C1C)",
              color: "white",
            }}
          >
            <AlertCircle size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-[#B91C1C] font-bold flex items-center gap-2">
              <span>Coupon error</span>
              <span className="font-mono text-[10px] px-1.5 py-[1px] rounded bg-white/70 border border-[#FCA5A5] text-[#7F1D1D]">
                {errorState.code}
              </span>
            </div>
            <div className="text-[13px] font-semibold text-[#7F1D1D] mt-0.5 flex items-center gap-2 flex-wrap">
              {trimmedCode && (
                <span className="font-mono">{trimmedCode}</span>
              )}
              <span className="text-[12px] font-normal text-[#991B1B]">
                {explainError(errorState.code, errorState.message)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={retry}
            className="h-8 px-2.5 inline-flex items-center gap-1 rounded-md text-[12px] font-semibold text-[#B91C1C] hover:bg-white/60"
          >
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // ---- Render: idle / previewing / preview ------------------------------
  return (
    <div className="rounded-xl border border-[#DBEAFE] bg-gradient-to-br from-[#F0F7FF] to-[#E6F0FE] mb-3 overflow-hidden">
      <div className="flex items-center gap-3 flex-wrap px-3.5 py-2.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-[0_2px_4px_rgba(37,99,235,0.2)]"
            style={{
              background: "linear-gradient(135deg, #2563EB, #1E40AF)",
              color: "white",
            }}
          >
            <Tag size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-[10.5px] font-bold tracking-wider uppercase text-[#1E40AF]">
              Coupon
            </div>
            <div className="text-[12px] font-semibold text-[#475569] mt-[1px] leading-tight">
              Have a code? Enter it to apply a discount.
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-[6px]" />

        <div className="flex items-center gap-1.5">
          <div
            className={`flex items-stretch rounded-md overflow-hidden border ${
              stage === "preview"
                ? "border-[#86EFAC] bg-white"
                : "border-[#BFDBFE] bg-white focus-within:border-[#2563EB] focus-within:ring-2 focus-within:ring-[#2563EB]/15"
            }`}
          >
            <input
              type="text"
              placeholder="CODE"
              value={codeInput}
              onChange={(e) => {
                setCodeInput(e.target.value);
                if (stage === "preview" || previewStale) {
                  setStage("idle");
                  setPreview(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (stage === "preview" && !previewStale) confirmApply();
                  else lookupPreview();
                }
              }}
              disabled={stage === "applying"}
              maxLength={32}
              className="h-9 w-[140px] px-2.5 text-[13px] font-semibold tracking-wider uppercase bg-transparent outline-none text-[#0F172A] font-mono"
            />
          </div>

          {stage !== "preview" && (
            <button
              type="button"
              onClick={lookupPreview}
              disabled={busy || !trimmedCode || billRefTotal <= 0}
              className="h-9 px-3.5 inline-flex items-center gap-1.5 font-bold rounded-md bg-primary text-white text-[13px] hover:bg-primary/90 disabled:bg-[#CBD5E1] disabled:cursor-not-allowed shadow-[0_1px_2px_rgba(39,125,254,0.25)]"
            >
              {busy ? "…" : (<><Ticket size={13} /> Apply</>)}
            </button>
          )}

          {stage === "preview" && (
            <>
              <button
                type="button"
                onClick={confirmApply}
                disabled={busy || previewStale}
                title={previewStale ? "Bill total changed — preview again." : undefined}
                className="h-9 px-3.5 inline-flex items-center gap-1.5 font-bold rounded-md bg-[#15803C] text-white text-[13px] hover:bg-[#0F6B30] disabled:opacity-50"
              >
                {busy ? "…" : (<><CheckCircle2 size={13} /> Confirm</>)}
              </button>
              <button
                type="button"
                onClick={() => {
                  setStage("idle");
                  setPreview(null);
                }}
                disabled={busy}
                className="h-9 px-2.5 inline-flex items-center gap-1 rounded-md text-[12px] font-semibold text-[#475569] hover:bg-white/60"
              >
                <XCircle size={13} /> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {stage === "preview" && preview && (
        <div className="px-3.5 pb-2.5 -mt-1">
          <div
            className={`flex items-center gap-2 text-[12px] rounded-md px-2.5 py-1.5 border ${
              previewStale
                ? "text-[#9A6700] bg-[#FFFBEB] border-[#FDE8C7]"
                : "text-[#15803C] bg-[#F0FBF4] border-[#BBE7CC]"
            }`}
          >
            {previewStale ? (
              <AlertCircle size={13} />
            ) : (
              <CheckCircle2 size={13} />
            )}
            <span className="flex-1 truncate">
              {preview.coupon_name && (
                <span className="font-semibold mr-1">{preview.coupon_name}</span>
              )}
              {previewStale
                ? "Bill total changed — press Apply again to refresh."
                : "Press Confirm to apply this discount."}
            </span>
            <span className="font-bold tabular-nums">
              − ₹{fmtINR(preview.discount_amount)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponPanel;
